import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Lock, ShieldCheck, Send, X, Check } from "lucide-react";
import { savePlan, submitPlanToOth } from "./plannerApi";
import { plannerTrack } from "./analytics";
import { executeCaptcha } from "../../lib/captcha";

const inputCls = "w-full bg-white border border-oth-line rounded-xl px-3.5 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors";

export default function SavePlanDialog({ open, onClose, buildPayload, contact, code, secret, onSaved }) {
    const [s1, setS1] = useState("");
    const [s2, setS2] = useState("");
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [emailQueued, setEmailQueued] = useState(true);
    const [err, setErr] = useState("");

    // Reset the local "submitted" flag every time the dialog is re-opened so a user who unlocked a
    // saved plan (or already submitted once in this session) can share their updated plan again.
    useEffect(() => {
        if (open) {
            setSubmitted(false);
            setErr("");
        }
    }, [open]);

    if (!open) return null;
    const saved = !!code;
    const link = `${window.location.origin}/diy/plan/${code}`;

    const doSave = async () => {
        setErr("");
        if (s1.length < 6) { setErr("Secret must be at least 6 characters."); return; }
        if (s1 !== s2) { setErr("The two secrets do not match."); return; }
        setSaving(true);
        try {
            const captchaToken = await executeCaptcha("diy_save");
            const res = await savePlan(s1, buildPayload(), captchaToken);
            onSaved(res.code, s1);
            toast.success("Plan saved securely.");
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(Array.isArray(d?.errors) ? d.errors.join(" ") : (typeof d === "string" ? d : "Could not save the plan."));
        } finally { setSaving(false); }
    };

    const copy = (text, label) => {
        navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied.`)).catch(() => toast.error("Copy failed — select and copy manually."));
    };

    const doSubmit = async () => {
        setSubmitting(true);
        try {
            const captchaToken = await executeCaptcha("diy_submit");
            const res = await submitPlanToOth(code, secret, contact, captchaToken);
            setSubmitted(true);
            setEmailQueued(res?.email_queued !== false);
            plannerTrack("plan_submitted");
            toast.success("Plan shared with One Tree Hill. We'll be in touch.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not submit the plan.");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="save-plan-dialog">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-lg rounded-3xl bg-white p-7 md:p-9 shadow-2xl max-h-[90vh] overflow-y-auto thin-scroll">
                <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-full border border-oth-line/70 flex items-center justify-center text-oth-ink3 hover:text-oth-ink" data-testid="save-dialog-close"><X size={15} /></button>

                {!saved ? (
                    <>
                        <div className="w-12 h-12 rounded-2xl bg-[#eef3f8] flex items-center justify-center text-oth-blue mb-4"><Lock size={20} /></div>
                        <h3 className="font-display text-xl font-semibold tracking-tight">Protect your plan with a secret</h3>
                        <p className="text-sm text-oth-ink3 font-body mt-2 leading-relaxed">
                            Your plan is encrypted with this secret before it is stored. Your unique code + this secret are the <strong>only</strong> way back in.
                        </p>
                        <div className="mt-4 rounded-2xl bg-[#fdf6ec] border border-[#e8d9bd] px-4 py-3 text-[13px] font-body text-[#7a5a1e] leading-relaxed" data-testid="secret-warning">
                            <strong>Important:</strong> there is no "forgot secret" or recovery of any kind. One Tree Hill has <strong>no access</strong> to your data unless you explicitly submit it to us. If you lose the secret, the plan is unrecoverable.
                        </div>
                        <div className="mt-5 space-y-4">
                            <input type="password" className={inputCls} placeholder="Choose a secret (min 6 characters)" value={s1} onChange={(e) => setS1(e.target.value)} data-testid="secret-input-1" />
                            <input type="password" className={inputCls} placeholder="Type the secret again" value={s2} onChange={(e) => setS2(e.target.value)} data-testid="secret-input-2" />
                            {err && <div className="text-sm text-red-600 font-body" data-testid="save-error">{err}</div>}
                            <button onClick={doSave} disabled={saving} className="btn-pill btn-pill-blue w-full justify-center text-sm disabled:opacity-50" data-testid="save-plan-confirm">
                                {saving ? "Encrypting & saving…" : "Encrypt & save my plan"}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 mb-4"><ShieldCheck size={20} /></div>
                        <h3 className="font-display text-xl font-semibold tracking-tight">Your plan is saved & encrypted</h3>
                        <p className="text-sm text-oth-ink3 font-body mt-2">Keep this code and your secret safe — together they unlock your plan.</p>

                        <div className="mt-5 rounded-2xl border border-oth-line bg-[#fafaf8] px-5 py-4 flex items-center justify-between gap-3">
                            <div className="font-display text-2xl font-semibold tracking-wide" data-testid="plan-code">{code}</div>
                            <button onClick={() => copy(code, "Code")} className="btn-pill btn-pill-dark text-xs" data-testid="copy-code-btn"><Copy size={13} /> Copy code</button>
                        </div>
                        <button onClick={() => copy(link, "Link")} className="mt-3 w-full text-left rounded-2xl border border-oth-line px-5 py-3 text-[13px] font-body text-oth-ink2 hover:border-oth-blue transition-colors break-all" data-testid="copy-link-btn">
                            {link} <span className="text-oth-blue ml-1">— tap to copy</span>
                        </button>

                        <div className="mt-6 pt-6 border-t border-oth-line/60">
                            <h4 className="font-display text-base font-semibold tracking-tight flex items-center gap-2"><Send size={15} className="text-oth-blue" /> Share with One Tree Hill (optional)</h4>
                            <p className="text-[13px] text-oth-ink3 font-body mt-1.5 leading-relaxed">
                                Only if you'd like our team to review your plan and reach out. This shares a copy of your plan and contact details with OTH. Until then, we cannot see any of it.
                            </p>
                            {submitted ? (
                                <div className="mt-4 space-y-2">
                                    <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-body text-emerald-700 flex items-center gap-2" data-testid="submitted-confirmation"><Check size={15} /> Shared with One Tree Hill. We'll be in touch shortly.</div>
                                    {!emailQueued && (
                                        <div className="rounded-2xl bg-[#fdf6ec] border border-[#e8d9bd] px-4 py-3 text-[13px] font-body text-[#7a5a1e]" data-testid="email-not-configured-note">
                                            Note: the email notification to OTH could not be sent (email sending is not configured on this site yet). Your plan is still safely visible to the OTH team in their dashboard.
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <button onClick={doSubmit} disabled={submitting} className="mt-4 btn-pill btn-pill-dark text-sm disabled:opacity-50" data-testid="submit-to-oth-btn">
                                    {submitting ? "Sharing…" : "Submit my plan to OTH"}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
